import React, { useMemo } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { cn } from "@/lib/utils";
import { toolbarTier } from "../document-toolbar/documentToolbarTiers";
import {
  getDocumentTypeTooltipContent,
  type CurrentTimelineDocumentDisplay,
} from "./timelineNodeAppearance";
import TimelineLegendShape from "./TimelineLegendShape";

type CurrentDocumentTypeIndicatorProps = {
  display: CurrentTimelineDocumentDisplay | null;
  className?: string;
};

const CurrentDocumentTypeIndicator: React.FC<
  CurrentDocumentTypeIndicatorProps
> = ({ display, className }) => {
  // Whether the label is on screen now depends on the toolbar's own width, so
  // no media query can answer it here. The tooltip therefore always names the
  // type, which is also what makes the compact toolbar readable.
  const tooltip = useMemo(
    () => (display ? getDocumentTypeTooltipContent(display, true) : null),
    [display]
  );

  if (!display) return null;

  const badge = (
    <span
      className={cn(
        "inline-flex min-w-0 max-w-full shrink items-center gap-1.5 rounded-md border border-border bg-input/30 px-2 py-1 text-xs text-foreground",
        className
      )}
      aria-label={`Dokumenttype: ${display.label}`}
    >
      <TimelineLegendShape
        shape={display.shape}
        fillClass={display.fillClass}
        size="sm"
      />
      <span className={cn("truncate", toolbarTier.fromLabelsInline)}>
        {display.label}
      </span>
    </span>
  );

  if (!tooltip) {
    return badge;
  }

  return (
    <TooltipProvider>
      <Tooltip delayDuration={300}>
        <TooltipTrigger asChild>{badge}</TooltipTrigger>
        <TooltipContent
          side="bottom"
          align="center"
          className="max-w-sm text-xs"
        >
          <div className="space-y-1.5 text-left">
            {tooltip.title ? (
              <p className="font-medium leading-snug">{tooltip.title}</p>
            ) : null}
            {tooltip.lines.map((line) => (
              <p
                key={line}
                className={cn("leading-snug", tooltip.title && "opacity-90")}
              >
                {line}
              </p>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default CurrentDocumentTypeIndicator;
